import React, { useCallback, useRef } from 'react';
import { type FormApi } from 'final-form';
import { Form } from 'react-final-form';
import { format } from 'date-fns';
import merge from 'lodash/merge';
import {
  Divider,
  InfoIcon,
  Stack,
  Tooltip,
  Typography,
} from '@cherre-frontend/ui';
import type { ProviderFormatSettings } from './types';
import {
  defaultProviderFormatSettings,
  providerDataSettingsOptions,
} from './constants';
import { ProviderSettingsFormatPreview } from './components/ProviderSettingsFormatPreview';
import { ProviderSettingsGrid } from './components/ProviderSettingsGrid';
import { ProviderSettingsFormField } from './components/ProviderSettingsFormField';
import {
  getCurrencyPreview,
  getDatePreview,
  getDateTimePreview,
  getNumberFormatPreview,
  getYearMonthPreview,
} from './preview-functions';

type PartialSettings = {
  [K in keyof ProviderFormatSettings]: Partial<ProviderFormatSettings[K]>;
};

interface ProviderSettingsTabProps {
  initialValues: PartialSettings;
  onSettingsChange: (values: ProviderFormatSettings) => void;
  onValidationChange: (isValid: boolean) => void;
}

const dateTimeFieldTooltip = [
  'HH: Two-digit hour',
  'mm: Two-digit minute',
  'ss: Two-digit second',
  'dd: Two-digit day of the month',
  'MM: Two-digit month',
  'yyyy: Four-digit year',
];

export const ProviderSettingsTab = ({
  initialValues,
  onSettingsChange,
  onValidationChange,
}: ProviderSettingsTabProps) => {
  const currentValuesRef = useRef<ProviderFormatSettings>();
  const initialValuesRef = useRef<ProviderFormatSettings>(
    merge({}, defaultProviderFormatSettings, initialValues)
  );

  const formChangesSubscriptionDecorator = useCallback(
    (form: FormApi<ProviderFormatSettings>) => {
      return form.subscribe(
        ({ values, valid }) => {
          onValidationChange(valid);
          if (values !== currentValuesRef.current) {
            currentValuesRef.current = values;
            onSettingsChange(values);
          }
        },
        { values: true, valid: true }
      );
    },
    [onSettingsChange, onValidationChange]
  );

  const dateTimeFieldValidator = (value: string) => {
    try {
      format(new Date(), value);
    } catch {
      return 'Invalid format';
    }
  };

  return (
    <Form
      onSubmit={() => {}}
      decorators={[formChangesSubscriptionDecorator]}
      initialValues={initialValuesRef.current}
    >
      {({ handleSubmit, values }) => (
        <Stack
          component='form'
          gap={2.5}
          marginTop={2.5}
          onSubmit={handleSubmit}
        >
          <Typography variant='body2'>
            Configure the formatting expected in provider submissions for
            ingestion and display preferences (validation and package reports).
          </Typography>

          <ProviderSettingsGrid>
            <Typography variant='h6' fontSize={14}>
              Custom Number Formatting
            </Typography>

            <ProviderSettingsFormatPreview
              value={getNumberFormatPreview(values)}
            />

            <ProviderSettingsFormField
              label='Thousands Separator:'
              name='numeric_formats.thousands_separator'
              subtitle='Character to use as the separator.'
              options={providerDataSettingsOptions.thousandsSeparator}
              isOptionDisabled={(option) =>
                option === values.numeric_formats.decimal_separator
              }
            />

            <ProviderSettingsFormField
              label='Decimal Separator:'
              name='numeric_formats.decimal_separator'
              subtitle='Character to use as the separator.'
              options={providerDataSettingsOptions.decimalSeparator}
              isOptionDisabled={(option) =>
                option === values.numeric_formats.thousands_separator
              }
            />

            <ProviderSettingsFormField
              label='Decimal Places:'
              name='numeric_formats.decimal_places'
              subtitle='Number of decimal places to display.'
              options={providerDataSettingsOptions.decimalPlaces}
            />

            <ProviderSettingsFormField
              label='Negative Sign:'
              name='numeric_formats.negative_sign'
              subtitle='Display negative values.'
              options={providerDataSettingsOptions.negativeSign}
            />
          </ProviderSettingsGrid>

          <Divider />

          <ProviderSettingsGrid>
            <Typography variant='h6' fontSize={14}>
              Custom Currency
            </Typography>

            <ProviderSettingsFormatPreview value={getCurrencyPreview(values)} />

            <ProviderSettingsFormField
              expand
              label='Currency Symbol:'
              name='numeric_formats.currency'
              subtitle='Currency symbol in a monetary value.'
              options={providerDataSettingsOptions.currencySymbol}
            />

            <ProviderSettingsFormField
              label='Currency Symbol Placement:'
              name='numeric_formats.currency_position'
              subtitle='Placement of the currency symbol before or after the value.'
              options={providerDataSettingsOptions.currencyPosition}
            />
          </ProviderSettingsGrid>

          <Divider />

          <ProviderSettingsGrid>
            <Typography variant='h6' fontSize={14} gridColumn='span 2'>
              Custom Date Formatting
            </Typography>

            <ProviderSettingsFormField
              label='Date Delimiter:'
              name='date_formats.separator'
              subtitle='Character used to separate date fields.'
              options={providerDataSettingsOptions.dateDelimiter}
            />

            <div />

            <ProviderSettingsFormField
              label='Date Format:'
              name='date_formats.date'
              subtitle='Order of month, day and year.'
              options={providerDataSettingsOptions.dateFormat}
            />

            <ProviderSettingsFormatPreview
              value={getDatePreview(values)}
              alignSelf='top'
            />

            <ProviderSettingsFormField
              label='Year Month Format:'
              name='date_formats.yearmonth'
              subtitle='Order of year and month.'
              options={providerDataSettingsOptions.yearMonthFormat}
            />

            <ProviderSettingsFormatPreview
              value={getYearMonthPreview(values)}
              alignSelf='top'
            />
          </ProviderSettingsGrid>

          <Divider />

          <ProviderSettingsGrid>
            <Typography variant='h6' fontSize={14}>
              Custom Date Time Formatting
            </Typography>

            <ProviderSettingsFormatPreview value={getDateTimePreview(values)} />
          </ProviderSettingsGrid>

          <ProviderSettingsFormField
            expand
            label='Date Time Format:'
            name='date_formats.datetime'
            subtitle='Order of time and date.'
            validator={dateTimeFieldValidator}
            icon={
              <Tooltip
                title={dateTimeFieldTooltip.map((line) => (
                  <React.Fragment key={line}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              >
                <InfoIcon color='primary' sx={{ fontSize: 18 }} />
              </Tooltip>
            }
          />
        </Stack>
      )}
    </Form>
  );
};
